<template>
  <div
    :style="getWidth()"
    class="correlation">
    <vue-c3
      :id="chartId"
      :ref="chartId"
      class="chart"
      :handler="handler">
    </vue-c3>
    <category-legend
      :id="legendId()"
      v-on:emphasizeColor="emphasizeColor"
      v-on:equalizeColors="equalizeColors"
      v-if="zCategory !== null"
      style="width: 100%"
      :values="zCategory.ranges"/>
  </div>
</template>

<script>
import Vue from 'vue';
import VueC3 from 'vue-c3';
import chroma from 'chroma-js';
import dayjs from 'dayjs';
import * as d3 from 'd3';
import CategoryLegend from '@/components/explorer/correlation/CategoryLegend';

require('c3/c3.css');

export default {
  name: 'ChartCorrelation',
  components: {
    VueC3,
    CategoryLegend
  },
  props: {
    chartId: {
      type: Number,
      default: 0
    },
    data: {
      type: Object,
      default: () => {}
    },
    meter: {
      type: String,
      default: 'divergent'
    },
    range: {
      type: Object,
      default: () => ({ x: [], y: [] })
    },
    zRange: {
      type: Array,
      default: () => []
    },
    zCategory: {
      type: Object,
      default: () => null
    },
    grid: {
      type: Boolean,
      default: false
    },
    x: {
      type: Number,
      default: -1
    },
    y: {
      type: Number,
      default: -1
    },
    z: {
      type: Number,
      default: -1
    },
    width: {
      type: Number,
      default: 0
    },
    height: {
      type: Number,
      default: 0
    }
  },
  watch: {
    data: {
      handler () {
        this.onDataChanged();
      },
      deep: true
    },
    range: {
      handler () {
        this.onDataChanged();
      },
      deep: true
    },
    zRange: {
      handler () {
        this.updateColors();
        this.onDataChanged();
      },
      deep: true
    },
    grid: {
      handler () {
        this.draw();
      }
    },
    meter: {
      handler () {
        this.updateColors();
        this.draw();
      }
    }
  },
  data () {
    return {
      handler: new Vue(),
      columns: [
        ['x'],
        ['y']
      ],
      intensity: [],
      scale: this.getInitialScale(),
      labelX: '',
      labelY: '',
      labelZ: '',
      dateArray: [],
      zValueArray: [],
      valueRanges: {}
    };
  },
  methods: {
    getWidth () {
      return 'width: ' + this.width + 'px';
    },
    legendId () {
      return 'legend' + this.chartId;
    },
    emphasizeColor (color) {
      const rgbColor = this.hexToRgb(color);
      const chartElement = document.getElementById(this.chartId);
      const circles = chartElement.getElementsByTagName('circle');

      Array.from(circles).forEach((x) => {
        if (Number(x.style.opacity) !== 0) {
          if (x.style.fill !== rgbColor) {
            x.style.setProperty('opacity', '0.2', 'important');
          } else {
            x.style.setProperty('opacity', '1', 'important');
          }
        }
      });
    },
    hexToRgb (hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? 'rgb(' + parseInt(result[1], 16) + ', ' + parseInt(result[2], 16) + ', ' + parseInt(result[3], 16) + ')' : null;
    },
    equalizeColors () {
      const chartElement = document.getElementById(this.chartId);
      const circles = chartElement.getElementsByTagName('circle');
      Array.from(circles).forEach((x) => {
        if (Number(x.style.opacity) !== 0) {
          x.style.setProperty('opacity', '0.5', 'important');
        }
      });
    },
    updateColors () {
      if (this.meter === 'divergent') {
        this.scale = chroma.scale(['#38aadb', '#ffa138']);
      } else {
        this.scale = chroma.scale(['#8BD0EE', '#057AAC']);
      }
    },
    getInitialScale () {
      if (this.meter === 'divergent') {
        return chroma.scale(['#38aadb', '#ffa138']);
      } else {
        return chroma.scale(['#8BD0EE', '#057AAC']);
      }
    },
    onDataChanged () {
      this.filterData(this.data);
      this.draw();
    },
    filterData (data) {
      const xDataArray = ['x'];
      const yDataArray = ['y'];

      if (data && data.variables) {
        let xVariableData = null;
        let yVariableData = null;
        let zVariableData = null;
        this.dateArray = [];
        this.intensity = [];
        this.zValueArray = [];

        for (let i = 0; i < data.variables.length; i++) {
          if (!xVariableData && data.variables[i].metadata.id === this.x) {
            xVariableData = data.variables[i].data.slice();
            this.labelX = data.variables[i].metadata.label;
            if (data.variables[i].metadata.unit) {
              this.labelX = this.labelX + ' (' + data.variables[i].metadata.unit + ')';
            }
          }
          if (!yVariableData && data.variables[i].metadata.id === this.y) {
            yVariableData = data.variables[i].data.slice();
            this.labelY = data.variables[i].metadata.label;
            if (data.variables[i].metadata.unit) {
              this.labelY = this.labelY + ' (' + data.variables[i].metadata.unit + ')';
            }
          }
          if (this.z !== -1 && !zVariableData && data.variables[i].metadata.id === this.z) {
            zVariableData = data.variables[i].data.slice();
            this.labelZ = data.variables[i].metadata.label;
            if (data.variables[i].metadata.unit) {
              this.labelZ = this.labelZ + ' (' + data.variables[i].metadata.unit + ')';
            }
          }
          if (xVariableData && yVariableData && (this.z === -1 || zVariableData)) {
            break;
          }
        }

        if (this.range.x.length === 0 && this.range.y.length === 0) {
          const xValueArray = xVariableData.map(xData => xData.value);
          const yValueArray = yVariableData.map(yData => yData.value);
          this.valueRanges = {
            x: [Math.min(...xValueArray), Math.max(...xValueArray)],
            y: [Math.min(...yValueArray), Math.max(...yValueArray)]
          };
        } else {
          this.valueRanges = this.range;
        }
        if (xVariableData && yVariableData && zVariableData) {
          // With intensity
          if (zVariableData.length > 0) {
            let intensityMinValue;
            let intensityMaxValue;
            if (this.zRange.length > 0) {
              intensityMinValue = this.zRange[0];
              intensityMaxValue = this.zRange[1];
            } else {
              intensityMinValue = zVariableData[0].value;
              intensityMaxValue = zVariableData[0].value;
              for (let i = 1; i < zVariableData.length; i++) {
                intensityMaxValue = Math.max(intensityMaxValue, zVariableData[i].value);
                intensityMinValue = Math.min(intensityMinValue, zVariableData[i].value);
              }
            }

            for (let i = 0; i < xVariableData.length; i++) {
              const yData = yVariableData.find(element => element.timestamp === xVariableData[i].timestamp);
              const zData = zVariableData.find(element => element.timestamp === xVariableData[i].timestamp);

              if (yData && zData) {
                // Filter by range and intensity
                const valueX = Number(xVariableData[i].value);
                const valueY = Number(yData.value);
                const valueZ = Number(zData.value);
                filterByRange(this.valueRanges, valueX, valueY, xDataArray, yDataArray, this.zRange, valueZ);
                const intensityValue = this.zCategory !== null
                  ? valueZ
                  : (valueZ - intensityMinValue) / (intensityMaxValue - intensityMinValue);

                if (inRange([intensityMinValue, intensityMaxValue], valueZ)) {
                  this.intensity.push(intensityValue);
                  this.dateArray.push(xVariableData[i].timestamp);
                  this.zValueArray.push(valueZ);
                }
              }
            }
          }
        } else if (xVariableData && yVariableData) {
          // Without intensity
          for (let i = 0; i < xVariableData.length; i++) {
            const yData = yVariableData.find(element => element.timestamp === xVariableData[i].timestamp);
            if (yData) {
              // Filter by range
              const valueX = Number(xVariableData[i].value);
              const valueY = Number(yData.value);
              filterByRange(this.valueRanges, valueX, valueY, xDataArray, yDataArray);
              this.dateArray.push(xVariableData[i].timestamp);
            }
          }
        }
      }

      this.columns = [
        xDataArray,
        yDataArray
      ];
    },
    color (index) {
      if (this.zValueArray.length > 0) {
        if (this.zCategory !== null) {
          const zValue = this.zValueArray[index];
          const range = this.zCategory.ranges.find(range => range.value === zValue);
          if (range) {
            return range.color;
          } else {
            return '#000000';
          }
        } else {
          const emptyrmalizedIntensity = (this.zValueArray[index] - Math.min(...this.zValueArray)) / (Math.max(...this.zValueArray) - Math.min(...this.zValueArray));
          return this.scale(emptyrmalizedIntensity).hex();
        }
      } else {
        return '#38aadb';
      }
    },
    draw () {
      const context = this;
      const options = {
        size: {
          height: this.height,
          width: this.width
        },
        data: {
          xSort: false,
          xs: {
            y: 'x'
          },
          columns: this.columns,
          type: 'scatter',
          color: function (color, d) {
            return context.color(d.index);
          }
        },
        axis: {
          x: {
            min: this.valueRanges.x !== undefined ? Number(this.valueRanges.x[0]) : undefined,
            max: this.valueRanges.x !== undefined ? Number(this.valueRanges.x[1]) : undefined,
            label: this.labelX,
            tick: {
              fit: false
            }
          },
          y: {
            min: this.valueRanges.y !== undefined ? Number(this.valueRanges.y[0]) : undefined,
            max: this.valueRanges.y !== undefined ? Number(this.valueRanges.y[1]) : undefined,
            label: this.labelY
          }
        },
        grid: {
          y: {
            show: this.grid
          }
        },
        zoom: {
          enabled: true
        },
        legend: {
          show: false
        },
        tooltip: {
          contents: function (d) {
            const index = d[0].index;
            const date = context.dateArray[index];
            const title = dayjs(date * 1000).format('YYYY-MM-DD hh:mm');
            const yValue = d[0].value;
            const yLabel = context.labelY;
            const xValue = d[0].x;
            const xLabel = context.labelX;

            const zValue = d3.format()(context.zValueArray[index]);
            const zLabel = context.labelZ;
            const thirdRow =
              '<tr>\n' +
              "<td class='name'>\n" +
              zLabel +
              '</td>\n' +
              "<td class='value'>\n" +
              zValue +
              '</td>\n' +
              '</tr>';
            return `<div class='c3-tooltip-container custom-tooltip'>
          <table class='c3-tooltip'>
            <tbody>
              <tr>
                <th colspan='2'>${title}</th>
              </tr>
              <tr>
                <td class='name'>
                  ${xLabel}
                </td>
                <td class='value'>
                  ${xValue}
                </td>
              </tr>
              <tr>
                <td class='name'>
                  ${yLabel}
                </td>
                <td class='value'>
                  ${yValue}
                </td>
              </tr>
                  ${context.zValueArray.length > 0 ? thirdRow : ''}
            </tbody>
          </table>
        </div>`;
          }
        }
      };
      this.handler.$emit('init', options);
    }
  }
};

const filterByRange = (range, valueX, valueY, xDataArray, yDataArray, zRange, valueZ) => {
  const insideIntensityRange = (typeof zRange !== 'undefined' && typeof valueZ !== 'undefined') ? inRange(zRange, valueZ) : true;
  let emptyIntensityRange;
  if (typeof zRange !== 'undefined' && typeof valueZ !== 'undefined') {
    emptyIntensityRange = (zRange.length === 0);
  } else {
    emptyIntensityRange = false;
  }

  if (insideIntensityRange) {
    xDataArray.push(valueX);
    yDataArray.push(valueY);
  } else if (!range.x || range.x.length === 0 || emptyIntensityRange || !range.y || range.y.length === 0) {
    xDataArray.push(valueX);
    yDataArray.push(valueY);
  }
};

const inRange = (range, value) => {
  return range && range.length === 2 && value >= range[0] && value <= range[1];
};

</script>
<style lang="scss">
  @import "../../../stylesheets/variables.scss";

  .correlation {
    display: inline-block;
  }
</style>



// WEBPACK FOOTER //
// src/components/explorer/correlation/ChartCorrelation.vue